// Canonical Help Center content, sourced from the approved
// "Bharat Estate Help Center: FAQs & Guide" PDF. Do not replace with
// unrelated FAQs. The full illustrated PDF is downloadable from the page.

export const HELP_PDF_URL = '/Bharat_Estate_Help_Center_Guide.pdf';

export const POPULAR_GUIDES = [
  { label: 'How to List Your Property', slug: 'list-property' },
  { label: 'How to Search for a Property', slug: 'search-properties' },
  { label: 'How to Use Map Search', slug: 'map-search' },
  { label: 'How to Contact a Seller', slug: 'contact-seller' },
  { label: 'EMI Calculator', slug: 'calculators' },
  { label: 'Premium Membership', slug: 'premium' },
  { label: 'Payment & Refund Help', slug: 'premium' },
  { label: 'Account / Login Help', slug: 'getting-started' },
  { label: 'Home Services', slug: 'home-services' },
  { label: 'Contact Support', slug: 'mobile-contact' },
];

export const SECTIONS = [
  {
    slug: 'getting-started',
    letter: 'A',
    icon: 'UserPlus',
    title: 'Getting Started & Your Account',
    blurb: 'Create your free account, sign in, set your city and manage your profile.',
    guides: [
      {
        title: 'Create your free account in under a minute',
        steps: [
          { t: 'Click Sign In → "Create Account"', d: 'Or go to bharat.estate/register. Prefer one click? Use Continue with Google.' },
          { t: 'Full Name', d: 'As you would like buyers and sellers to see it.' },
          { t: 'Email', d: 'This becomes your login. It can\'t be changed later.' },
          { t: 'Phone Number', d: 'Optional but recommended. Type your own number; the grey text is only a sample.' },
          { t: 'I am a', d: 'Choose Buyer / Tenant, Property Owner or Real Estate Agent.' },
          { t: 'Password & Confirm Password', d: 'Use the icon to check what you typed.' },
          { t: 'Click Create Account', d: 'You\'re in, and your Founders\' free month of Premium is activated.' },
        ],
      },
      {
        title: 'Your Dashboard: everything in one place',
        steps: [
          { t: 'List New Property', d: 'Jump straight to the listing form.' },
          { t: 'Your numbers', d: 'Listed properties, saved properties, total inquiries and total views.' },
          { t: 'Tabs', d: 'Overview · My Properties · Saved · Inquiries · AI Advisor · Invoices · My Bookings · Settings.' },
          { t: 'Recent activity', d: 'Latest listings, newest inquiries and your conversations.' },
        ],
      },
    ],
    faqs: [
      { q: 'Do I need an account to browse?', a: 'No. Search, Map Search, calculators and every listing page are open to all. You need an account to save homes, post a listing and use your dashboard.' },
      { q: 'Is creating an account free?', a: 'Yes. Signing up is free, and every new member gets the first month of Premium free (Founders\' launch offer).' },
      { q: 'Can I sign up with Google?', a: 'Yes. "Continue with Google" is on both the Sign In and Create Account pages.' },
      { q: 'Which fields are mandatory at sign-up?', a: 'Full Name, Email, Password and Confirm Password. Phone is optional but helps people reach you.' },
      { q: 'What should I pick in "I am a"?', a: 'Buyer / Tenant if you\'re looking, Property Owner if you\'re selling or renting out your own property, Real Estate Agent if you list for clients. You can change it later in Settings.' },
      { q: 'Can I change my email address?', a: 'No. Email can\'t be changed once the account is created. To use a different email, create a new account.' },
      { q: 'How do I change my city?', a: 'Click Select City (top-left) → use current location, search, or pick a popular city.' },
      { q: 'Do I have to share my location?', a: 'No. It\'s optional and only used to show homes near you. Choose "Select city" instead.' },
      { q: 'How do I update my name or phone?', a: 'Dashboard → Settings → Profile Settings → edit → Save.' },
      { q: 'I can\'t sign in. What now?', a: 'Use Continue with Google if that\'s how you joined. Otherwise contact support: contact@bharat.estate or call +91 94650 24516.' },
      { q: 'What\'s the "Premium is free" pop-up?', a: 'The Founders\' launch offer. Click Claim My Free Month or Continue exploring to close it.' },
      { q: 'Is there a mobile app?', a: 'The Bharat Estate app is coming soon. The website works fully on your phone\'s browser in the meantime.' },
    ],
  },
  {
    slug: 'list-property',
    letter: 'B',
    icon: 'PlusCircle',
    title: 'List Your Property',
    blurb: 'Post your home for sale or rent, add the map pin, photos and amenities.',
    guides: [
      {
        title: 'Basic information: title, type, sale or rent, description',
        steps: [
          { t: 'Property Title', d: 'e.g. "Luxurious 3BHK Apartment in Bandra West".' },
          { t: 'Property Type', d: 'Apartment, Villa, Independent House, Plot/Land, Commercial Space or Others (specify).' },
          { t: 'Listing Type', d: 'For Sale (default) or For Rent.' },
          { t: 'Description', d: 'Layout, view, society, connectivity, nearby schools and metro.' },
        ],
      },
      {
        title: 'Location & the exact map pin',
        steps: [
          { t: 'Address, City, State, Pincode', d: 'e.g. 123, ABC Society, Bandra West · Mumbai · Maharashtra · 400050.' },
          { t: 'Search a landmark', d: 'Society, landmark or area to place the pin.' },
          { t: 'Locate address', d: 'Uses the address + city you typed.' },
          { t: 'My location', d: 'Drops the pin where you are (allow location).' },
          { t: 'Or click the map', d: 'Then drag the pin to the exact gate or tower.' },
        ],
      },
      {
        title: 'Check everything, then click List Property',
        steps: [
          { t: 'Images added?', d: 'Check every thumbnail loads. Hover one to remove it.' },
          { t: 'Virtual tour (optional)', d: 'Paste the link if you have one.' },
          { t: 'List Property', d: 'Submit. You\'ll see "Property listed successfully" and land in Dashboard → My Properties.' },
        ],
      },
    ],
    faqs: [
      { q: 'Who can list a property?', a: 'Owners, authorised agents and developers with a Bharat Estate account and an active Premium membership.' },
      { q: 'Which fields are mandatory?', a: 'Title, Property Type, Listing Type, Description, Price, Area, Address, City, State, Pincode and the map pin.' },
      { q: 'Why is the map pin compulsory?', a: 'Buyers see your pin and get directions. Without it you\'ll see "Please pin the property location on the map".' },
      { q: 'Is posting a property free?', a: 'Your first month of Premium is free, so you can list at no cost during that month. After that, a paid plan is needed to keep listings live. Plans start from ₹99/month.' },
      { q: 'How many properties can I list?', a: 'It depends on your plan. The Founders membership includes unlimited Sell / Rent listings. If you hit your plan\'s limit you\'ll see "Listing quota exhausted": upgrade or top up.' },
      { q: 'How do I enter the price?', a: 'Digits only: 15000000 for ₹1.5 Cr. For rent, enter the monthly amount, e.g. ₹55000, and set Price Type to Per Month (Rent).' },
      { q: 'Can I upload photos from my phone?', a: 'The form takes image links. Paste a direct image URL and click Add (up to 10).' },
      { q: 'Can I add a video or 360° tour?', a: 'Yes. Paste a Matterport, YouTube or similar link in Virtual Tour URL.' },
      { q: 'How soon does my listing go live?', a: 'It\'s saved instantly to My Properties. Owner listings are reviewed by the team before going live, and the Verified badge appears after review.' },
      { q: 'How do I edit or delete a listing?', a: 'Dashboard → My Properties → Edit (then Update Property) or Delete (confirm the prompt).' },
      { q: 'Where do I see buyer enquiries?', a: 'Dashboard → Inquiries, with name, contact, property, message and date.' },
      { q: 'Does Bharat Estate charge brokerage when I sell?', a: 'No. 0% brokerage from Bharat Estate. Owners pay one transparent membership fee.' },
      { q: 'What happens when my plan expires?', a: 'You\'ll see "Your plan has expired". Memberships don\'t auto-renew, so renew from your dashboard to keep listing. Existing listings stay active until expiry.' },
      { q: 'Can I list a property I don\'t own?', a: 'Only with legal authority (e.g. as the owner\'s authorised agent). Listings without authority are prohibited.' },
      { q: 'What gets a listing removed?', a: 'Misleading info, false pricing, duplicates, properties in dispute or without authority. Removal can happen without notice or refund.' },
      { q: 'How do I get the Verified badge?', a: 'Fill in accurate details. Owner listings are reviewed by the team; developer projects show RERA details where available.' },
      { q: 'Can NRIs list from abroad?', a: 'Yes. NRI desk: +1 (365) 275-2286, and the concierge can coordinate visits and paperwork.' },
    ],
  },
  {
    slug: 'search-properties',
    letter: 'C',
    icon: 'Search',
    title: 'Search & Discover',
    blurb: 'Find homes by city, locality, budget and BHK — plus AI-powered search.',
    guides: [
      {
        title: 'Quick search from the home page',
        steps: [
          { t: 'Pick a tab', d: 'Buy, Rent or Commercial.' },
          { t: 'Type a location', d: 'City, locality, landmark or project, then pick a suggestion.' },
          { t: 'Property type', d: 'All types, Apartment, Independent House, Villa or Plot.' },
          { t: 'Budget', d: 'Under ₹50 Lac up to ₹5 Cr+.' },
          { t: 'Click Search or Map', d: 'See matching listings as a list or on the map.' },
        ],
      },
      {
        title: 'Your results page: filters, sort & views',
        steps: [
          { t: 'Title & count', d: 'Shows what you searched and how many matches.' },
          { t: 'Sort', d: 'Newest first, Price Low→High, High→Low, Most viewed.' },
          { t: 'List · Grid · Map', d: 'Switch how results are shown.' },
          { t: 'Filters panel', d: 'Looking to, Property type, City, Bedrooms, Budget, Furnishing → Apply. Clear all resets.' },
        ],
      },
      {
        title: 'Bharat AI: describe your dream home in plain English',
        steps: [
          { t: 'Type what you want', d: 'e.g. "3BHK with a balcony in Bangalore, close to good schools and tech parks".' },
          { t: 'Click Ask AI', d: 'Bharat AI scans every verified listing.' },
          { t: 'Get 3 personalised matches', d: 'Each with a short reason why it fits you.' },
        ],
      },
    ],
    faqs: [
      { q: 'Is Bharat Estate free for buyers and tenants?', a: 'Yes. Browsing, saving, enquiring and contacting sellers is free, with 0% brokerage on direct-owner listings.' },
      { q: 'What can I type in the search box?', a: 'A city, locality, landmark or project name. Pick a suggestion from the list for the most accurate results.' },
      { q: 'How do I search only rentals?', a: 'Choose the Rent tab on the home page, or Rent in the top bar or the filters panel.' },
      { q: 'How do I filter by BHK or furnishing?', a: 'On the results page use Bedrooms (1-5+ BHK) and Furnishing (Furnished/Semi/Unfurnished), then Apply.' },
      { q: 'How do I remove a filter?', a: 'Click x on its chip above the results, or Clear all in the filters panel.' },
      { q: 'Can I sort by price?', a: 'Yes: Newest first, Price Low→High, Price High→Low or Most viewed.' },
      { q: 'What does "From ₹1.3 Cr" mean?', a: 'It\'s a developer project; that\'s the starting price of its smallest configuration.' },
      { q: 'What is "Near me"?', a: 'A shortcut chip that shows homes around your current location (your browser will ask permission).' },
      { q: 'Where are new launches?', a: 'Projects in the top bar, the New Projects chip, or "New Launch Projects" on the home page.' },
      { q: 'Does the listed price include stamp duty?', a: 'Usually not. Use the Stamp Duty calculator and keep a 5-10% buffer for registration and other costs.' },
      { q: 'No results. What should I try?', a: 'Widen the budget, remove the type filter, try nearby localities, or zoom out on Map Search.' },
      { q: 'Can Bharat AI find homes for me?', a: 'Yes. Describe what you want in plain English and get 3 hand-picked matches, each with a reason.' },
    ],
  },
  {
    slug: 'map-search',
    letter: 'C',
    icon: 'Map',
    title: 'Map Search',
    blurb: 'Browse every listing on a live price map and search any neighbourhood.',
    guides: [
      {
        title: 'Map Search: explore every neighbourhood',
        steps: [
          { t: 'Filter bar', d: 'Buy & Rent, type, BHK, budget.' },
          { t: '"N properties in view"', d: 'Updates as you pan and zoom.' },
          { t: 'List view', d: 'Switch back to a regular list any time.' },
          { t: 'Live price pins', d: 'Each pin shows the price. Click one to preview the listing.' },
          { t: 'Locate me', d: 'Centre the map on where you are.' },
          { t: 'Listing panel', d: 'Cards for everything visible on the map.' },
        ],
      },
    ],
    faqs: [
      { q: 'How do I zoom the map?', a: 'On desktop use the + / – buttons, your mouse wheel or trackpad, or double-click. On mobile pinch to zoom with two fingers. Drag to pan.' },
      { q: 'Why do some listings not show a pin?', a: 'A pin appears once the lister has set the exact location. Listings without coordinates still appear in the list panel.' },
      { q: 'What does "Search this area" do?', a: 'It re-runs the search for whatever the map currently shows, so you can explore a specific pocket.' },
    ],
  },
  {
    slug: 'contact-seller',
    letter: 'D',
    icon: 'Phone',
    title: 'Property Pages & Contacting Sellers',
    blurb: 'Understand a listing page, contact the seller, request a callback or site visit.',
    guides: [
      {
        title: 'Anatomy of a listing page',
        steps: [
          { t: 'Price & listing ID', d: 'Plus /sq.ft and "Price negotiable" if the seller allows it.' },
          { t: 'Key facts', d: 'Bedrooms, bathrooms, area, parking, furnishing, age, possession, developer.' },
          { t: 'Call', d: 'Ring the lister directly.' },
          { t: 'WhatsApp', d: 'Opens a chat with the property link pre-filled.' },
          { t: 'Enquire · Callback · Visit', d: 'Three ways to reach out.' },
        ],
      },
      {
        title: 'Request a callback or schedule a site visit',
        steps: [
          { t: 'Callback tab', d: 'Add your name, phone and best time to call → Request callback.' },
          { t: 'Visit tab', d: 'Add your details, pick a date and preferred time → Schedule visit.' },
          { t: 'View on Google Maps / Get Directions', d: 'In the Location section for exact navigation on visit day.' },
        ],
      },
    ],
    faqs: [
      { q: 'How do I contact a seller or landlord?', a: 'On the listing page use Call, WhatsApp or the Enquire form. Cards in search results have call and WhatsApp icons too.' },
      { q: 'Who exactly am I contacting?', a: 'The contact card shows the lister\'s name and role (Owner, Agent or Developer) and a Verified tag.' },
      { q: 'Is contacting free?', a: 'Yes, for buyers and tenants. There\'s no brokerage on direct-owner listings.' },
      { q: 'Can I ask for a call at a specific time?', a: 'Yes. Use the Callback tab and add your best time to call.' },
      { q: 'How do I book a site visit?', a: 'Use the Visit tab: pick a date, add your preferred time and questions, then click Schedule visit.' },
      { q: 'Can I get floor plans?', a: 'Project pages have Download Brochure. For other listings, ask in your enquiry.' },
      { q: 'Why can\'t I save a property?', a: 'Saving needs an account. Sign in, then tap Save ♡ again.' },
      { q: 'Where do I see homes I saved?', a: 'Dashboard → Saved. Remove any you no longer want.' },
      { q: 'Can Bharat Estate help with paperwork?', a: 'Premium members get concierge support for site visits, paperwork and legal help, plus stamp-duty and registration guidance.' },
      { q: 'What happens after I send an enquiry?', a: 'It goes to the lister, who will contact you on the details you shared. By sending it you agree to be contacted about that property.' },
      { q: 'How do I find the exact location?', a: 'Scroll to Location and use View on Google Maps or Get Directions.' },
      { q: 'What does "Price negotiable" mean?', a: 'The lister is open to offers. Discuss it on the call or visit.' },
    ],
  },
  {
    slug: 'premium',
    letter: 'E',
    icon: 'Crown',
    title: 'Premium Membership & Billing',
    blurb: 'Your free first month, paid plans, invoices, refunds and billing help.',
    guides: [
      {
        title: 'Founders\' offer: Premium is free for your first month',
        steps: [
          { t: 'Claim My Free Month', d: 'On bharat.estate/premium, or the pop-up you see on your first visit.' },
          { t: 'Already have an account?', d: 'Sign in — existing users are upgraded automatically.' },
          { t: 'What\'s included', d: 'Unlimited Sell/Rent listings, owner contacts and concierge, valid 1 month from sign-up.' },
        ],
      },
    ],
    faqs: [
      { q: 'Is there any hidden charge?', a: 'No. Premium is free for the first month for every member, and there\'s no card on file during the trial.' },
      { q: 'What happens after the first month?', a: 'Premium becomes chargeable. Pick a plan to keep listing and contacting owners. You pay only when you choose to continue.' },
      { q: 'How much are the paid plans?', a: 'Plans start from ₹99/month, with monthly, quarterly and yearly options. See View Plans in your dashboard for current prices.' },
      { q: 'Do plans auto-renew?', a: 'No. Memberships don\'t auto-renew, so renew before expiry to keep your listings live.' },
      { q: 'I signed up earlier. Am I eligible?', a: 'Yes. Existing users were upgraded automatically. Sign in and look for the Founders badge on your dashboard.' },
      { q: 'Where can I find my invoices?', a: 'Dashboard → Invoices.' },
      { q: 'Can I cancel?', a: 'Any time. Your premium features and existing listings stay active until the expiry date; no refund for the remaining period.' },
      { q: 'Can I get a refund?', a: 'Premium purchases are final and non-refundable. Exceptions: double charge, payment taken but membership not activated, or an unauthorised transaction.' },
      { q: 'How do I request an exception refund?', a: 'Within 48 hours of payment, email contact@bharat.estate with subject "Refund Request - [your email]" plus your transaction ID and proof. Reply in 3-5 business days.' },
      { q: 'Money deducted but Premium not active?', a: 'Contact support right away. They\'ll verify it and either activate your plan or refund in 7-10 business days.' },
      { q: 'What if I delete my account mid-plan?', a: 'Remaining premium benefits are forfeited, with no refund.' },
      { q: 'Why is Bharat Estate giving this away?', a: 'To build India\'s most trusted real-estate community. Founding members use the platform, share feedback and help it grow.' },
    ],
  },
  {
    slug: 'calculators',
    letter: 'F',
    icon: 'Calculator',
    title: 'Tools & Calculators',
    blurb: 'EMI, affordability, stamp duty, rental yield, market insights, compare and more.',
    guides: [
      {
        title: 'EMI Calculator: plan your home-loan instalment',
        steps: [
          { t: 'Loan Amount', d: 'Type it or drag the slider (₹1 Lac – ₹10 Cr). From a listing, it\'s pre-filled with the property price.' },
          { t: 'Interest Rate (% p.a.)', d: '5% – 20%. Use the rate your bank quotes.' },
          { t: 'Loan Tenure', d: '1–30 years, entered in months (e.g. 240 = 20 years).' },
          { t: 'Monthly EMI', d: 'Updates instantly, with a principal vs interest breakdown.' },
        ],
      },
      {
        title: 'Compare Properties: up to 4 homes, side by side',
        steps: [
          { t: 'Open Compare', d: 'Resources → Compare Properties, or bharat.estate/compare.' },
          { t: 'Add Property', d: 'Pick from the list (add at least 2).' },
          { t: 'Read the table', d: 'Price, Area, ₹/sq.ft, Bedrooms, Bathrooms, Parking, Floor, Furnishing, Facing, Age, Location, Amenities.' },
        ],
      },
    ],
    faqs: [
      { q: 'Are the tools free?', a: 'Yes. All calculators, quizzes, the glossary, market insights and compare are free, no sign-in needed.' },
      { q: 'Are the results exact?', a: 'They\'re planning estimates. Confirm loan terms with your bank and stamp duty with the Sub-Registrar.' },
      { q: 'How do I enter loan tenure in the EMI tool?', a: 'In months: 240 = 20 years, 180 = 15 years. Or use the slider.' },
      { q: 'Why does a longer tenure lower my EMI?', a: 'You repay over more months, but total interest goes up. The breakdown shows both.' },
      { q: 'What EMI is "safe"?', a: 'The affordability tool suggests keeping EMI below 40% of in-hand income, with a 5-10% buffer for stamp duty & registration.' },
      { q: 'Does stamp duty differ for women buyers?', a: 'Many states offer a 1-2% rebate. Choose the buyer type in the calculator to see the effect.' },
      { q: 'What\'s a good rental yield?', a: 'India\'s average is about 2-4%; above 3% net in metros is considered very good.' },
      { q: 'How fresh is Market Insights data?', a: 'It shows the quarter it covers and uses indicative averages from listed properties.' },
      { q: 'How many homes can I compare?', a: 'Up to 4, and at least 2 to see the table.' },
      { q: 'Where\'s the EMI tool on a listing?', a: 'Scroll to "Property Tools & Insights" on any property page and click EMI Calculator — the property price is pre-filled.' },
      { q: 'What\'s the difference between the two quizzes?', a: 'Property Match Quiz finds homes for you (6 questions). Knowledge Quiz tests what you know (10 questions).' },
      { q: 'Do I get recommendations after the quiz?', a: 'Yes: See Matching Properties. Sign in for personalised AI suggestions.' },
    ],
  },
  {
    slug: 'home-services',
    letter: 'G',
    icon: 'Wrench',
    title: 'Home Services, Movers & Pros',
    blurb: 'Book trusted home services, get a movers quote or join as a verified pro.',
    guides: [
      {
        title: 'Book trusted home services in 3 steps',
        steps: [
          { t: 'Choose a service', d: '21 categories: AC, cleaning, pest control, electrician, plumber, carpenter, painting, interiors and more.' },
          { t: 'Book a slot', d: 'Pick a date & time. The pro calls to confirm, and you pay only after the job is done.' },
          { t: 'Get it done', d: 'A verified pro arrives. You rate the job. Bookings show in Dashboard → My Bookings.' },
        ],
      },
      {
        title: 'Moving home? Get a quote in 30 seconds',
        steps: [
          { t: 'From City & To City', d: 'Local or intercity, 24 cities.' },
          { t: 'Home Size', d: '1RK/Studio to 4 BHK / Villa, or small and large offices.' },
          { t: 'Get My Quote', d: 'Instant estimate range. No commitment, no spam.' },
        ],
      },
    ],
    faqs: [
      { q: 'What services can I book?', a: '21 categories, including AC, home & kitchen cleaning, sofa cleaning, pest control, electrician, plumber, carpenter, painting, salon, massage, appliance repair, smart home, disinfection, gardening, home inspection, interiors, renovation, laundry and vehicle services.' },
      { q: 'When do I pay?', a: 'Pay only after the job is done. Always agree on a written estimate first.' },
      { q: 'How are pros verified?', a: 'Aadhaar + police check, plus training on Bharat Estate SOPs. Still check the pro\'s ID on arrival.' },
      { q: 'Can I call a pro directly?', a: 'Yes. Category pages list verified pros with Call and WhatsApp. Tap to reveal the number.' },
      { q: 'Is home inspection useful before buying?', a: 'Very. Pre-move-in checks cover structure, plumbing, electrical and dampness, with thermal imaging on bigger homes.' },
      { q: 'Where do I see my bookings?', a: 'Dashboard → My Bookings.' },
      { q: 'How accurate is the movers quote?', a: 'It\'s an instant estimate range. The free pre-move survey locks the final price.' },
      { q: 'Is my move insured?', a: 'Transit insurance up to ₹15 lakh on the Premium tier, plus a 7-day damage claim window.' },
      { q: 'Is there a fee to join as a pro?', a: 'No joining fee and no CV. Apply in 60 seconds.' },
      { q: 'How fast can a pro start?', a: 'Typically 7 days: apply, verify, train, trial job, live.' },
      { q: 'Can I list myself in a category directory?', a: 'Yes, free. Use "Register as a Pro" or apply via Become a Pro.' },
      { q: 'A pro misbehaved. What do I do?', a: 'Report it via the Grievance page (choose the closest category) or call support.' },
    ],
  },
  {
    slug: 'privacy-support',
    letter: 'H',
    icon: 'ShieldCheck',
    title: 'Safety, Privacy & Support',
    blurb: 'Stay safe, understand your data rights, raise a grievance or delete your account.',
    guides: [
      {
        title: 'Raise a grievance: and know the timelines',
        steps: [
          { t: 'Grievance Officer', d: 'contact@bharat.estate · +91 94650 24516.' },
          { t: 'Resolution timeline', d: '24 hrs acknowledgement · 48 hrs action plan · 7 days standard · 30 days complex.' },
          { t: 'Your details + category', d: 'Account, Payment/Billing, Premium, Property Listing, Fraud/Scam, Technical, Privacy, Other.' },
          { t: 'Subject + Description → Submit', d: 'Include dates, transaction IDs and screenshots.' },
        ],
      },
    ],
    faqs: [
      { q: 'Are listings verified?', a: 'Developer projects come from official developer channels and carry RERA details where available. Owner listings are reviewed by the team before going live.' },
      { q: 'How do I check a project\'s RERA registration?', a: 'Find the RERA number in the listing\'s Highlights and search it on your state\'s official RERA website.' },
      { q: 'Should I pay a token amount online?', a: 'Not before visiting the property and checking documents. Be wary of anyone rushing you to pay.' },
      { q: 'How do I report a fake listing or scam?', a: 'Grievance page → category Fraud / Scam Report, with the listing ID and details.' },
      { q: 'Does Bharat Estate sell my data?', a: 'No. The privacy policy says personal information isn\'t sold to third parties for marketing.' },
      { q: 'Who can see my contact details?', a: 'Listing details are public. Your contact info may be shared with interested buyers or renters about that listing.' },
      { q: 'What cookies are used?', a: 'Essential, analytics and preference cookies. You can control them in your browser.' },
      { q: 'Can I download or correct my data?', a: 'Yes. You can access, correct, port or delete your data. Email contact@bharat.estate.' },
      { q: 'How long does deletion take?', a: 'Up to 30 days, with an email confirmation when it\'s done.' },
      { q: 'I don\'t remember my login. Can I still delete?', a: 'Yes. Use the request form with the email linked to your account.' },
      { q: 'How fast are grievances handled?', a: 'Acknowledged in 24 hrs, action plan in 48 hrs, resolved in 7 days (standard) or 30 days (complex).' },
      { q: 'A payment issue: what should I include?', a: 'Your transaction ID and payment receipt, so the team can trace it quickly.' },
    ],
  },
  {
    slug: 'mobile-contact',
    letter: 'I',
    icon: 'Smartphone',
    title: 'Mobile, Quick Fixes & Contact',
    blurb: 'Using Bharat Estate on your phone, quick fixes and how to reach us.',
    guides: [
      {
        title: 'Bharat Estate on your phone',
        steps: [
          { t: 'Help', d: 'Opens Bharat AI chat from the top-right on any page.' },
          { t: 'Menu & Search', d: 'All pages and tools; search by city, locality, landmark or project.' },
          { t: 'Map & Select City', d: 'Jump to Map Search or set your city from the menu.' },
          { t: 'Sticky bar', d: 'Call · Chat (WhatsApp) · Enquire, always at the bottom of a listing.' },
        ],
      },
    ],
    faqs: [
      { q: 'How do I contact support?', a: 'Email contact@bharat.estate or call +91 94650 24516 (India). NRI desk: +1 (365) 275-2286. You can also tap the gold Help button to chat with Bharat AI.' },
      { q: 'The page looks broken — what should I try first?', a: 'Refresh the page, check your internet connection, and make sure your browser is up to date. If it persists, contact support with a screenshot.' },
      { q: 'A button isn\'t responding.', a: 'Close any open pop-up, scroll it fully into view, and tap once. On very old browsers, try the latest Chrome or Safari.' },
      { q: 'How do I chat on WhatsApp?', a: 'Open the Help chat and tap "Chat on WhatsApp", or use the WhatsApp button on any listing.' },
    ],
  },
];

export const findSection = (slug) => SECTIONS.find((s) => s.slug === slug);
